import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import DataTable from '../../components/DataTable';
import PeriodPicker from '../../components/PeriodPicker';
import { useAuth } from '../../context/AuthContext';

const READINESS_LABELS = { in_progress: 'В работе', completed: 'Завершена', on_hold: 'Приостановлена', cancelled: 'Отменена' };
const money = (n) => `${Math.round(Number(n || 0)).toLocaleString('ru')} ₽`;
const date = (d) => d ? new Date(d).toLocaleDateString('ru') : '—';

function DealForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', contract_number: '', contract_date: '', customer_name: '', object_name: '', city: '', client_source: '', budget: '', readiness_status: 'in_progress' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Укажите название проекта'); return; }
    setSaving(true);
    try {
      await api.post('/finance/deals', { ...form, budget: form.budget ? parseFloat(form.budget) : 0 });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h2>Новый проект</h2><button className="modal-close" onClick={onClose}>×</button></div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group"><label>Название проекта *</label><input value={form.name} onChange={e => set('name', e.target.value)} required /></div>
          <div className="form-row">
            <div className="form-group"><label>№ договора</label><input value={form.contract_number} onChange={e => set('contract_number', e.target.value)} /></div>
            <div className="form-group"><label>Дата договора</label><input type="date" value={form.contract_date} onChange={e => set('contract_date', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Заказчик / объект</label><input value={form.customer_name} onChange={e => set('customer_name', e.target.value)} /></div>
            <div className="form-group"><label>Город</label><input value={form.city} onChange={e => set('city', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Источник клиента</label><input value={form.client_source} onChange={e => set('client_source', e.target.value)} /></div>
            <div className="form-group"><label>Бюджет, ₽</label><input type="number" step="0.01" value={form.budget} onChange={e => set('budget', e.target.value)} /></div>
          </div>
          <div className="form-group">
            <label>Статус готовности</label>
            <select value={form.readiness_status} onChange={e => set('readiness_status', e.target.value)}>
              {Object.entries(READINESS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Сохранение...' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeletedLogModal({ onClose }) {
  const [logs, setLogs] = useState(null);

  useEffect(() => { api.get('/finance/deals/deleted/log').then(r => setLogs(r.data)); }, []);

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header"><h2>Журнал удалений проектов</h2><button className="modal-close" onClick={onClose}>×</button></div>
        {logs === null ? <div className="loading-text">Загрузка...</div> : logs.length === 0 ? (
          <div className="empty-state">Проекты пока не удалялись</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Когда</th><th>Кто</th><th>Проект</th><th>Причина</th></tr></thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id}>
                    <td>{new Date(l.created_at).toLocaleString('ru')}</td>
                    <td>{l.user_name || '—'}</td>
                    <td>{l.details?.name || '—'}</td>
                    <td>{l.details?.comment || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="modal-actions"><button className="btn-secondary" onClick={onClose}>Закрыть</button></div>
      </div>
    </div>
  );
}

export default function Deals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = user.role === 'admin' || user.role === 'accountant';
  const canExport = ['admin', 'accountant', 'rop'].includes(user.role);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDeletedLog, setShowDeletedLog] = useState(false);
  const [period, setPeriod] = useState({ from: null, to: null });
  const [marginMin, setMarginMin] = useState('');
  const [marginMax, setMarginMax] = useState('');
  // Фильтры таблицы переживают переход в карточку сделки и обратно — иначе после «К сделкам»
  // список каждый раз возвращается нефильтрованным, что неудобно при работе с длинным списком.
  const [filters, setFilters] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dealsFilters')) || {}; } catch { return {}; }
  });
  useEffect(() => { sessionStorage.setItem('dealsFilters', JSON.stringify(filters)); }, [filters]);

  const load = () => { setLoading(true); api.get('/finance/deals').then(r => setDeals(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const inPeriod = useMemo(() => {
    if (!period.from && !period.to) return deals;
    return deals.filter(d => {
      if (!d.contract_date) return false;
      const date = d.contract_date.slice(0, 10);
      if (period.from && date < period.from) return false;
      if (period.to && date > period.to) return false;
      return true;
    });
  }, [deals, period]);

  // Маржинальность — маржа относительно оплаченной заказчиком суммы (та же формула, что и на
  // дашборде в «Ниже маржи X%»); без оплат считать процент не от чего, такие проекты фильтром
  // по марже не трогаем.
  const marginPercent = (d) => (Number(d.paid) > 0 ? (Number(d.margin) / Number(d.paid)) * 100 : null);

  const filtered = useMemo(() => {
    const min = marginMin !== '' ? Number(marginMin) : null;
    const max = marginMax !== '' ? Number(marginMax) : null;
    if (min == null && max == null) return inPeriod;
    return inPeriod.filter(d => {
      const p = marginPercent(d);
      if (p == null) return false;
      if (min != null && p < min) return false;
      if (max != null && p > max) return false;
      return true;
    });
  }, [inPeriod, marginMin, marginMax]);

  const columns = [
    { key: 'created_at', label: 'Дата создания', render: d => date(d.created_at), sortValue: d => d.created_at || '', filterable: false },
    { key: 'name', label: 'Проект', render: d => d.name },
    { key: 'manager_name', label: 'Менеджер', render: d => d.manager_name || '—' },
    { key: 'customer_name', label: 'Заказчик', render: d => d.customer_name || '—' },
    { key: 'city', label: 'Город', render: d => d.city || '—' },
    { key: 'client_source', label: 'Источник', render: d => d.client_source || '—' },
    { key: 'contracting_entity', label: 'Юр.лицо', render: d => d.contracting_entity || '—' },
    { key: 'budget', label: 'Бюджет', render: d => money(d.budget), sortValue: d => Number(d.budget), filterable: false },
    { key: 'paid', label: 'Оплачено', render: d => money(d.paid), sortValue: d => Number(d.paid), filterable: false },
    { key: 'remainder', label: 'Остаток', render: d => money(d.remainder), sortValue: d => Number(d.remainder), filterable: false },
    { key: 'margin', label: 'Денежный результат', render: d => <span style={{ color: d.margin >= 0 ? 'var(--success)' : 'var(--danger)' }}>{money(d.margin)}</span>, sortValue: d => Number(d.margin), filterable: false },
    {
      key: 'margin_percent', label: 'Денежный результат, %',
      render: d => {
        const p = marginPercent(d);
        return p == null ? '—' : <span style={{ color: p >= 0 ? 'var(--success)' : 'var(--danger)' }}>{p.toFixed(0)}%</span>;
      },
      sortValue: d => marginPercent(d),
      filterable: false,
    },
    { key: 'prepayment_date', label: 'Дата предоплаты', render: d => date(d.prepayment_date), sortValue: d => d.prepayment_date || '', filterable: false },
    { key: 'payment_date', label: 'Дата оплаты', render: d => date(d.payment_date), sortValue: d => d.payment_date || '', filterable: false },
  ];

  return (
    <div>
      <div className="toolbar">
        <PeriodPicker onChange={setPeriod} storageKey="dealsPeriod" />
        <span className="text-muted" style={{ fontSize: 12 }}>по дате договора</span>
        <label className="text-muted" style={{ fontSize: 12, marginLeft: 12 }}>Денежный результат, % от</label>
        <input type="number" value={marginMin} onChange={e => setMarginMin(e.target.value)} placeholder="—" style={{ width: 70 }} />
        <label className="text-muted" style={{ fontSize: 12 }}>до</label>
        <input type="number" value={marginMax} onChange={e => setMarginMax(e.target.value)} placeholder="—" style={{ width: 70 }} />
        <div className="toolbar-spacer" />
        {user.role === 'admin' && <button className="btn-secondary btn-sm" onClick={() => setShowDeletedLog(true)}>Журнал удалений</button>}
        {canCreate && <button className="btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Новый проект</button>}
      </div>
      {loading ? <div className="loading-text">Загрузка...</div> : (
        <DataTable
          columns={columns}
          rows={filtered}
          initialSort={{ key: 'name', dir: 'asc' }}
          onRowClick={d => navigate(`/projects/deal/${d.id}`)}
          emptyText="Проектов не найдено"
          filters={filters}
          onFiltersChange={setFilters}
          canExport={canExport}
          exportFilename="Проекты"
        />
      )}
      {showForm && canCreate && <DealForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {showDeletedLog && <DeletedLogModal onClose={() => setShowDeletedLog(false)} />}
    </div>
  );
}
